import React from 'react';
import { Link } from 'react-router-dom';
import { FiCheck, FiCircle, FiMail, FiUserCheck, FiClipboard } from 'react-icons/fi';
import './RegisterOnboardingFlow.css';

const STATE_LABEL = {
  done: 'Done',
  sent: 'Sent',
  pending: 'Pending',
  created: 'Created',
  current: 'Current',
};

function StepIcon({ step }) {
  const type = step.step_type || step.stage_key;
  if (type === 'welcome') return <FiMail aria-hidden />;
  if (type === 'questionnaire' || type === 'questionnaire_done') return <FiClipboard aria-hidden />;
  if (type === 'candidate') return <FiUserCheck aria-hidden />;
  if (['done', 'sent', 'created'].includes(step.state)) return <FiCheck aria-hidden />;
  return <FiCircle aria-hidden />;
}

/**
 * Horizontal onboarding pipeline for a register candidate (requisition-style).
 */
export default function RegisterOnboardingFlow({
  onboarding,
  onMarkQuestionnaireSent,
  onMarkQuestionnaireCompleted,
  onClearQuestionnaireCompleted,
  onOpenOnboard,
  busyAction = null,
  compact = false,
}) {
  const steps = onboarding?.steps || [];
  if (!steps.length) return null;

  return (
    <div className={`reg-onboarding${compact ? ' reg-onboarding--compact' : ''}`}>
      {!compact && (
        <div className="reg-onboarding-header">
          <h2>Onboarding pipeline</h2>
          <p className="form-hint">
            Progress through Sign Up → Welcome → Questionnaire → Complete → Candidate.
            Welcome is automatic after signup; questionnaire completion is marked by staff;
            candidate is created via Candidates → Onboard from Register.
          </p>
          {onboarding?.blocked && (
            <p className="reg-onboarding-banner reg-onboarding-banner--danger">
              Status is Disabled — outreach stopped.
            </p>
          )}
          {onboarding?.on_hold && (
            <p className="reg-onboarding-banner reg-onboarding-banner--info">
              On Future follow-up hold.
            </p>
          )}
        </div>
      )}

      <ol className="reg-onboarding-track" aria-label="Register onboarding steps">
        {steps.map((step, idx) => {
          const done = ['done', 'sent', 'created'].includes(step.state);
          const stateClass = step.current
            ? 'current'
            : done
              ? step.state
              : 'pending';
          return (
            <li
              key={step.stage_key || idx}
              className={`reg-onboarding-step reg-onboarding-step--${stateClass}`}
            >
              <div className="reg-onboarding-step-node">
                <span className="reg-onboarding-step-icon">
                  <StepIcon step={step} />
                </span>
                {idx < steps.length - 1 && <span className="reg-onboarding-connector" aria-hidden />}
              </div>
              <div className="reg-onboarding-step-body">
                <span className="reg-onboarding-step-label">{step.label}</span>
                <span className={`reg-onboarding-step-state reg-state--${step.state}`}>
                  {STATE_LABEL[step.state] || step.state}
                </span>
                {!compact && step.detail ? (
                  <span className="reg-onboarding-step-detail">{step.detail}</span>
                ) : null}
              </div>
            </li>
          );
        })}
      </ol>

      {!compact && (
        <div className="reg-onboarding-actions req-workflow-actions">
          <div className="req-workflow-buttons">
            {!onboarding.questionnaire_sent && onMarkQuestionnaireSent && (
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                disabled={Boolean(busyAction)}
                onClick={onMarkQuestionnaireSent}
              >
                {busyAction === 'q-sent' ? 'Saving…' : 'Mark questionnaire sent'}
              </button>
            )}
            {!onboarding.questionnaire_completed && onMarkQuestionnaireCompleted && (
              <button
                type="button"
                className="btn btn-primary btn-sm"
                disabled={Boolean(busyAction)}
                onClick={onMarkQuestionnaireCompleted}
              >
                {busyAction === 'q-done' ? 'Saving…' : 'Mark questionnaire completed'}
              </button>
            )}
            {onboarding.questionnaire_completed && onClearQuestionnaireCompleted && (
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                disabled={Boolean(busyAction)}
                onClick={onClearQuestionnaireCompleted}
              >
                Undo questionnaire complete
              </button>
            )}
            {!onboarding.candidate_created && onOpenOnboard && (
              <button
                type="button"
                className="btn btn-primary btn-sm"
                disabled={Boolean(busyAction)}
                onClick={onOpenOnboard}
              >
                Create as candidate
              </button>
            )}
            {onboarding.candidate_created && onboarding.created_user_id ? (
              <Link
                className="btn btn-secondary btn-sm"
                to={`/candidates/${onboarding.created_user_id}`}
              >
                Open candidate #{onboarding.created_user_id}
              </Link>
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
}
